import { ReactNode, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Add, Remove } from '@mui/icons-material'
import VenueRow, { VenueRowSpace } from './VenueRow'

interface Props<Extra extends VenueRowSpace> {
  onConfirm: () => void
  confirmLabel: string
  confirmDisabled?: boolean
  disabled?: boolean
  onSkip?: () => void
  skipLabel?: string
  shortlistedSpaces: Extra[]
  onRemoveVenue: (space: Extra) => void
  pinned?: VenueRowSpace | null
  countSuffix?: string
  alwaysShowList?: boolean
  defaultOpen?: boolean
  status?: ReactNode
  className?: string
}

const DEFAULT_CHROME = `fixed bottom-0 left-0 right-0 z-10 bg-white border-t border-[#f0f0f0]
  shadow-[0px_-4px_25px_0px_rgba(89,89,89,0.25)] rounded-t-2xl`

const AVATAR = 'w-6 h-6 rounded-full border border-white shrink-0 bg-[#f0f0f0] bg-cover bg-center'
const CONFIRM = `!bg-[#af231c] !text-white rounded-[4px] !px-[16px] !py-[8px] font-[Montserrat]
  !font-semibold !text-[16px] !leading-[24px] cursor-pointer hover:!bg-[#8b1a15] hover:shadow-lg
  disabled:opacity-50 disabled:cursor-not-allowed transition-all`
const SKIP = `!border !border-[#595959] !text-[#595959] !bg-transparent rounded-[4px] !px-[16px] !py-[8px]
  font-[Montserrat] !font-semibold !text-[16px] !leading-[24px] cursor-pointer hover:shadow-lg
  disabled:opacity-50 disabled:cursor-not-allowed transition-all`

const ActionBar = <Extra extends VenueRowSpace>({
  onConfirm,
  confirmLabel,
  confirmDisabled = false,
  disabled = false,
  onSkip,
  skipLabel = 'Skip - Let the experts choose',
  shortlistedSpaces,
  onRemoveVenue,
  pinned,
  countSuffix = 'shortlisted',
  alwaysShowList = false,
  defaultOpen = false,
  status,
  className,
}: Props<Extra>) => {
  const [open, setOpen] = useState(defaultOpen)
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return

    const publishHeight = () =>
      document.documentElement.style.setProperty('--action-bar-height', `${bar.offsetHeight}px`)

    publishHeight()
    const observer = new ResizeObserver(publishHeight)
    observer.observe(bar)

    return () => {
      observer.disconnect()
      document.documentElement.style.removeProperty('--action-bar-height')
    }
  }, [])

  const contentWidth = className ? 'w-full' : 'w-[90vw]'

  const listed: VenueRowSpace[] = pinned ? [pinned, ...shortlistedSpaces] : shortlistedSpaces
  const count = listed.length
  const showList = alwaysShowList || count > 0

  const venueRows = (
    <>
      {pinned && <VenueRow space={pinned} badge='Your venue' />}
      {shortlistedSpaces.map((space) => (
        <VenueRow key={space.id} space={space} onRemove={() => onRemoveVenue(space)} />
      ))}
    </>
  )

  const avatars = (
    <div className='flex -space-x-2 shrink-0'>
      {listed.slice(0, 3).map((space) => (
        <div
          key={space.id}
          className={AVATAR}
          style={space.images[0] ? { backgroundImage: `url("${space.images[0]}")` } : {}}
        />
      ))}
    </div>
  )

  const countText = (
    <span className='text-sm font-semibold text-[#AF231C] truncate'>
      {count} {count === 1 ? 'venue' : 'venues'} {countSuffix}
    </span>
  )

  const toggleIcon = open ? (
    <Remove className='size-5! text-[#8c8c8c] shrink-0' />
  ) : (
    <Add className='size-5! text-[#8c8c8c] shrink-0' />
  )

  const accordion = (contentClassName: string) => (
    <AnimatePresence initial={false}>
      {open && count > 0 && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className='overflow-hidden w-full'>
          <div
            className={`${contentClassName} [&::-webkit-scrollbar]:hidden`}
            style={{ scrollbarWidth: 'none' }}>
            {venueRows}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )

  const confirmButton = (extra = '') => (
    <button
      type='button'
      onClick={onConfirm}
      disabled={disabled || confirmDisabled}
      className={`${CONFIRM} ${extra}`}>
      {confirmLabel}
    </button>
  )

  const skipButton = (extra = '') =>
    onSkip && (
      <button type='button' onClick={onSkip} disabled={disabled} className={`${SKIP} ${extra}`}>
        {skipLabel}
      </button>
    )

  return (
    <div ref={barRef} className={className ?? DEFAULT_CHROME}>
      {(!open || count === 0) && status}

      {showList && (
        <div className='sm:hidden px-4 pt-2 pb-2'>
          {accordion('max-h-[50vh] overflow-y-auto flex flex-col gap-2 px-3 py-3')}
          <button
            type='button'
            onClick={() => setOpen((isOpen) => !isOpen)}
            disabled={count === 0}
            aria-expanded={open}
            className={`w-full flex items-center justify-between gap-3 pt-1 pb-2 bg-transparent
              cursor-pointer disabled:cursor-default`}>
            <div className='flex items-center gap-3 min-w-0'>
              {avatars}
              {countText}
            </div>
            {count > 0 && toggleIcon}
          </button>
        </div>
      )}

      <div className='sm:hidden flex flex-col gap-2 px-4 pt-2 pb-3'>
        {confirmButton('w-full')}
        {skipButton('w-full')}
      </div>

      <div className='hidden sm:flex justify-center pb-4'>
        <div className={`flex flex-col items-center ${contentWidth}`}>
          {accordion(
            `max-h-[296px] overflow-y-auto px-4 pt-2 pb-4 grid ${
              count === 1 ? 'grid-cols-1' : 'grid-cols-2'
            } gap-3 ${contentWidth} max-w-[792px] mx-auto bg-white`
          )}
          <div
            className={`flex items-center ${contentWidth} shrink-0 ${
              onSkip ? 'gap-16' : 'justify-center gap-4'
            }`}>
            {onSkip && <div className='flex-1 flex justify-end'>{skipButton()}</div>}
            <div
              className={`flex flex-row items-center gap-4 ${
                onSkip ? 'flex-1 justify-start' : ''
              }`}>
              {confirmButton()}
              {showList && (
                <button
                  type='button'
                  onClick={() => setOpen((isOpen) => !isOpen)}
                  disabled={count === 0}
                  aria-expanded={open}
                  className='flex items-center gap-3 cursor-pointer disabled:cursor-default'>
                  {count > 0 && avatars}
                  {countText}
                  {count > 0 && toggleIcon}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActionBar
